import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Upload, CheckCircle2, AlertCircle, XCircle, ArrowDown } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { useT } from "@/hooks/useT";
import { useLocale } from "@/hooks/useLocale";
import { sha256Hex, computeMatchState, abbrevHash, isPdfFile, PDF_ACCEPT } from "./sha256";

const DATE_LOCALE = { en: "en-SG", zh: "zh-Hans-SG" };

function fmtDate(locale, isoDate) {
  if (!isoDate) return "";
  try {
    const d = typeof isoDate === "string" && isoDate.length === 10
      ? new Date(`${isoDate}T00:00:00Z`)
      : new Date(isoDate);
    return new Intl.DateTimeFormat(DATE_LOCALE[locale] || "en-SG", {
      day: "2-digit", month: "short", year: "numeric",
    }).format(d);
  } catch (_err) {
    return String(isoDate);
  }
}

const RESULT_PALETTE = {
  green: {
    icon: CheckCircle2,
    border: "border-[rgba(46,160,89,0.6)]",
    bg: "bg-[rgba(46,160,89,0.12)]",
    accent: "text-[#7CDCA0]",
  },
  amber: {
    icon: AlertCircle,
    border: "border-[rgba(220,150,0,0.6)]",
    bg: "bg-[rgba(220,150,0,0.12)]",
    accent: "text-[#F5C67B]",
  },
  red: {
    icon: XCircle,
    border: "border-[rgba(220,60,60,0.6)]",
    bg: "bg-[rgba(220,60,60,0.12)]",
    accent: "text-[#F0A3A3]",
  },
};

function Result({ data }) {
  const { t } = useT("verifiedDocuments");
  const locale = useLocale();
  const reduce = useReducedMotion();
  const palette = RESULT_PALETTE[data.state];
  const Icon = palette.icon;

  const Wrapper = reduce ? "div" : motion.div;
  const wrapperProps = reduce
    ? {}
    : {
        initial: { opacity: 0, y: 24 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.6, ease: [0.2, 0.8, 0.2, 1] },
      };

  return (
    <Wrapper
      {...wrapperProps}
      role="status"
      aria-live="polite"
      className={`mt-6 rounded-[16px] border ${palette.border} ${palette.bg} p-6`}
      data-testid={`verifier-result-${data.state}`}
    >
      <div className="flex items-start gap-4">
        <Icon className={`mt-0.5 h-6 w-6 shrink-0 ${palette.accent}`} strokeWidth={1.75} aria-hidden />
        <div className="flex-1">
          <p className={`text-base font-semibold leading-snug ${palette.accent}`}>
            {t(`verifier.result${data.state === "green" ? "Green" : data.state === "amber" ? "Amber" : "Red"}.title`)}
          </p>
          {data.state === "green" && (
            <p className="mt-2 text-sm leading-relaxed text-ink-secondary">
              {t("verifier.resultGreen.sub", {
                date: fmtDate(locale, data.matched.publication_date),
              })}
            </p>
          )}
          {data.state === "amber" && (
            <>
              <p className="mt-2 text-sm leading-relaxed text-ink-secondary">
                {t("verifier.resultAmber.sub", {
                  date: fmtDate(locale, data.matched.publication_date),
                  currentVersion: data.current?.version ?? "—",
                })}
              </p>
              {data.current && (
                <div className="mt-4">
                  <Button
                    as="a"
                    href="#published-documents"
                    variant="secondary-on-dark"
                    size="sm"
                    data-testid="verifier-amber-cta"
                  >
                    {t("verifier.resultAmber.cta")}
                  </Button>
                </div>
              )}
            </>
          )}
          {data.state === "red" && (
            <>
              <p className="mt-2 text-sm leading-relaxed text-ink-secondary">
                {t("verifier.resultRed.sub")}
              </p>
              <div className="mt-4">
                <p className="text-[11px] font-semibold uppercase tracking-caps text-ink-tertiary">
                  {t("verifier.resultRed.fingerprintLabel")}
                </p>
                <code className="mt-1 block break-all font-mono text-[12px] text-ink-primary">
                  {data.hash}
                </code>
              </div>
            </>
          )}
        </div>
      </div>
    </Wrapper>
  );
}

/**
 * Verifier — drag-and-drop + file-picker, SHA-256 fingerprinting via
 * Web Crypto API, three-state result rendering.
 *
 * The hashing is local-only: the PDF never leaves the browser. We prove
 * this at the network layer by never calling fetch/XHR with the file
 * body — see the handleFile() path.
 *
 * The disabled state fires when `documents` is empty (initial launch,
 * nothing published yet). The drop zone still renders but is inert and
 * the caption explains why.
 */
export function Verifier({ documents }) {
  const { t } = useT("verifiedDocuments");
  const inputRef = useRef(null);
  const dropRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [state, setState] = useState({ phase: "idle" }); // idle|checking|result|error
  const [typeError, setTypeError] = useState("");

  const empty = !documents || documents.length === 0;

  const handleFile = useCallback(
    async (file) => {
      setTypeError("");
      if (!file) return;
      if (!isPdfFile(file)) {
        setTypeError(t("verifier.wrongType"));
        return;
      }
      setState({ phase: "checking" });
      try {
        const buf = await file.arrayBuffer();
        const hex = await sha256Hex(buf);
        const result = computeMatchState(hex, documents);
        setState({ phase: "result", data: result });
      } catch (_err) {
        setState({ phase: "idle" });
        setTypeError(t("verifier.wrongType"));
      }
    },
    [documents, t],
  );

  const openPicker = useCallback(() => {
    if (empty || state.phase === "checking") return;
    inputRef.current?.click();
  }, [empty, state.phase]);

  const onInputChange = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      if (file) handleFile(file);
      // Reset the input value so the same file can be re-selected after a reset.
      e.target.value = "";
    },
    [handleFile],
  );

  // Drag-and-drop plumbing — tolerate missing DataTransfer in jsdom.
  const onDragOver = useCallback(
    (e) => {
      if (empty) return;
      e.preventDefault();
      setDragging(true);
    },
    [empty],
  );
  const onDragLeave = useCallback(() => setDragging(false), []);
  const onDrop = useCallback(
    (e) => {
      if (empty) return;
      e.preventDefault();
      setDragging(false);
      const file = e.dataTransfer?.files?.[0];
      if (file) handleFile(file);
    },
    [empty, handleFile],
  );

  // Keyboard: Enter/Space on the drop zone opens the native picker.
  const onKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        openPicker();
      }
    },
    [openPicker],
  );

  // Clean up a dangling drag state if the user drops outside the window.
  useEffect(() => {
    const off = () => setDragging(false);
    window.addEventListener("dragend", off);
    window.addEventListener("drop", off);
    return () => {
      window.removeEventListener("dragend", off);
      window.removeEventListener("drop", off);
    };
  }, []);

  const reset = useCallback(() => {
    setState({ phase: "idle" });
    setTypeError("");
  }, []);

  const reduce = useReducedMotion();
  const dropMotion = reduce
    ? {}
    : {
        animate: dragging ? { scale: 1.02 } : { scale: 1 },
        transition: { duration: 0.2, ease: [0.2, 0.8, 0.2, 1] },
      };

  return (
    <section
      className="border-t border-hairline py-16 md:py-20"
      data-testid="verified-verifier"
    >
      <div className="mx-auto max-w-4xl px-6">
        <h2 className="font-sans text-3xl font-bold leading-tight tracking-tight sm:text-4xl">
          {t("verifier.heading")}
        </h2>
        <p className="mt-4 max-w-2xl text-sm leading-relaxed text-ink-secondary">
          {t("verifier.helperVerbatim")}
        </p>

        <motion.div
          ref={dropRef}
          {...dropMotion}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          onClick={openPicker}
          onKeyDown={onKeyDown}
          role="button"
          tabIndex={empty ? -1 : 0}
          aria-label={t("verifier.dropAriaLabel")}
          aria-disabled={empty}
          className={[
            "mt-8 flex flex-col items-center justify-center rounded-[16px] border p-8 text-center transition-colors duration-base ease-out",
            "bg-[rgba(2,4,11,0.5)]",
            empty
              ? "cursor-not-allowed border-dashed border-hairline opacity-60"
              : "cursor-pointer border-dashed",
            dragging ? "border-active-border" : "border-hairline",
            !empty &&
              "hover:border-active-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-frac-violet focus-visible:ring-offset-2 focus-visible:ring-offset-surface-base",
          ].join(" ")}
          data-testid="verifier-dropzone"
          data-dragging={dragging ? "true" : "false"}
          data-empty={empty ? "true" : "false"}
        >
          {empty ? (
            <>
              <ArrowDown className="h-7 w-7 text-ink-tertiary" strokeWidth={1.5} aria-hidden />
              <p className="mt-4 text-base font-semibold text-ink-primary">
                {t("verifier.disabled.label")}
              </p>
              <p className="mt-2 max-w-md text-sm text-ink-secondary">
                {t("verifier.disabled.caption")}
              </p>
            </>
          ) : state.phase === "checking" ? (
            <>
              <div className="h-7 w-7 animate-pulse rounded-full border-2 border-frac-violet" aria-hidden />
              <p className="mt-4 text-sm text-ink-secondary">
                {t("verifier.computing")}
              </p>
            </>
          ) : (
            <>
              <Upload className="h-7 w-7 text-frac-violet" strokeWidth={1.5} aria-hidden />
              <p className="mt-4 text-base font-semibold text-ink-primary md:hidden">
                {t("verifier.dropLabelMobile")}
              </p>
              <p className="mt-4 hidden text-base font-semibold text-ink-primary md:block">
                {t("verifier.dropLabel")}
              </p>
              <div className="mt-5">
                <Button
                  variant="secondary-on-dark"
                  size="sm"
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    openPicker();
                  }}
                  data-testid="verifier-choose-file"
                >
                  {t("verifier.chooseFile")}
                </Button>
              </div>
            </>
          )}
          <input
            ref={inputRef}
            type="file"
            accept={PDF_ACCEPT}
            onChange={onInputChange}
            className="sr-only"
            data-testid="verifier-input"
            tabIndex={-1}
          />
        </motion.div>

        {typeError && (
          <p
            className="mt-4 text-sm text-[#F0A3A3]"
            role="status"
            data-testid="verifier-type-error"
          >
            {typeError}
          </p>
        )}

        {state.phase === "result" && <Result data={state.data} />}

        {state.phase === "result" && (
          <div className="mt-5">
            <Button
              variant="secondary-on-dark"
              size="sm"
              onClick={reset}
              data-testid="verifier-reset"
            >
              {t("verifier.reset")}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}

export default Verifier;
